import React, { useCallback, useEffect, useState } from 'react';
import {
  Badge, Box, Button, Divider, FormControl, FormHelperText, FormLabel,
  HStack, IconButton, Input, Modal, ModalBody, ModalCloseButton, ModalContent,
  ModalFooter, ModalHeader, ModalOverlay, NumberDecrementStepper,
  NumberIncrementStepper, NumberInput, NumberInputField, NumberInputStepper,
  Select, Switch, Tab, TabList, TabPanel, TabPanels, Table, Tabs, Tbody, Td,
  Text, Textarea, Th, Thead, Tooltip, Tr, VStack, useDisclosure, useToast,
} from '@chakra-ui/react';
import { AddIcon, CopyIcon, DeleteIcon, EditIcon, ViewIcon } from '@chakra-ui/icons';
import { useTranslation } from 'react-i18next';
import PageShell from '../components/PageShell';
import emailBookingsService from '../services/emailBookingsService';
import roomTypeService from '../services/roomTypeService';

const STATUS_COLORS = {
  parsed_created: 'green', parsed_modified: 'blue', parsed_cancelled: 'gray',
  needs_review: 'yellow', ignored: 'gray', error: 'red',
};

const emptySourceForm = {
  name: '', parser: 'generic_kv', allowedSenderDomains: '', defaultRoomType: '',
  defaultSource: 'ota', priceMode: 'total_from_email', currency: '',
  isActive: true, autoCreate: true, fieldMap: '{}',
};

export default function EmailBookings() {
  const { t } = useTranslation();
  const toast = useToast();

  const [tabIndex, setTabIndex] = useState(0);
  const [sources, setSources] = useState([]);
  const [messages, setMessages] = useState([]);
  const [roomTypes, setRoomTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [statusFilter, setStatusFilter] = useState('');
  const [sourceFilter, setSourceFilter] = useState('');

  const sourceModal = useDisclosure();
  const [sourceForm, setSourceForm] = useState(emptySourceForm);
  const [editingSourceId, setEditingSourceId] = useState(null);
  const [saving, setSaving] = useState(false);

  const [testSubject, setTestSubject] = useState('');
  const [testBody, setTestBody] = useState('');
  const [testResult, setTestResult] = useState(null);
  const [testError, setTestError] = useState('');
  const [testing, setTesting] = useState(false);

  const importModal = useDisclosure();
  const [importingMessage, setImportingMessage] = useState(null);
  const [importForm, setImportForm] = useState({});
  const [importing, setImporting] = useState(false);

  const detailModal = useDisclosure();
  const [viewingMessage, setViewingMessage] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = {};
      if (statusFilter) params.status = statusFilter;
      if (sourceFilter) params.sourceId = sourceFilter;
      const [sourcesRes, messagesRes, roomTypesRes] = await Promise.all([
        emailBookingsService.listSources(),
        emailBookingsService.listMessages(params),
        roomTypeService.getAll(),
      ]);
      setSources(sourcesRes || []);
      setMessages(messagesRes || []);
      setRoomTypes(roomTypesRes?.results || roomTypesRes || []);
    } catch (e) {
      setError(t('emailBookings.page.title'));
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [t, statusFilter, sourceFilter]);

  useEffect(() => { load(); }, [load]);

  const copyAddress = async (address) => {
    try {
      await navigator.clipboard.writeText(address);
      toast({ title: t('emailBookings.sources.copied'), status: 'success', duration: 2000 });
    } catch (e) { /* clipboard unavailable — nothing more we can do here */ }
  };

  const openCreateSource = () => {
    setEditingSourceId(null);
    setSourceForm(emptySourceForm);
    setTestSubject(''); setTestBody(''); setTestResult(null); setTestError('');
    sourceModal.onOpen();
  };

  const openEditSource = (source) => {
    setEditingSourceId(source.id);
    setSourceForm({
      name: source.name,
      parser: source.parser,
      allowedSenderDomains: (source.allowedSenderDomains || []).join('\n'),
      defaultRoomType: source.defaultRoomType ? String(source.defaultRoomType) : '',
      defaultSource: source.defaultSource,
      priceMode: source.priceMode,
      currency: source.currency || '',
      isActive: source.isActive,
      autoCreate: source.autoCreate,
      fieldMap: JSON.stringify(source.fieldMap || {}, null, 2),
    });
    setTestSubject(''); setTestBody(''); setTestResult(null); setTestError('');
    sourceModal.onOpen();
  };

  const saveSource = async () => {
    let fieldMap;
    try {
      fieldMap = sourceForm.fieldMap.trim() ? JSON.parse(sourceForm.fieldMap) : {};
    } catch (e) {
      toast({ title: t('emailBookings.sources.drawer.fieldMapInvalid'), status: 'error' });
      return;
    }
    const payload = {
      name: sourceForm.name,
      parser: sourceForm.parser,
      allowedSenderDomains: sourceForm.allowedSenderDomains
        .split('\n').map((d) => d.trim()).filter(Boolean),
      defaultRoomType: sourceForm.defaultRoomType ? Number(sourceForm.defaultRoomType) : null,
      defaultSource: sourceForm.defaultSource,
      priceMode: sourceForm.priceMode,
      currency: sourceForm.currency,
      isActive: sourceForm.isActive,
      autoCreate: sourceForm.autoCreate,
      fieldMap,
    };
    setSaving(true);
    try {
      if (editingSourceId) {
        await emailBookingsService.updateSource(editingSourceId, payload);
        toast({ title: t('emailBookings.sources.toasts.updated'), status: 'success', duration: 2500 });
      } else {
        await emailBookingsService.createSource(payload);
        toast({ title: t('emailBookings.sources.toasts.created'), status: 'success', duration: 2500 });
      }
      sourceModal.onClose();
      load();
    } catch (e) {
      toast({
        title: t('emailBookings.sources.toasts.saveFailed'),
        description: e?.response?.data?.detail, status: 'error', duration: 4000,
      });
    } finally {
      setSaving(false);
    }
  };

  const deleteSource = async (source) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(t('emailBookings.sources.deleteConfirm'))) return;
    try {
      await emailBookingsService.deleteSource(source.id);
      toast({ title: t('emailBookings.sources.toasts.deleted'), status: 'success', duration: 2500 });
      load();
    } catch (e) {
      toast({ title: t('emailBookings.sources.toasts.deleteFailed'), status: 'error' });
    }
  };

  const runTestParse = async () => {
    if (!editingSourceId) return;
    setTesting(true);
    setTestError('');
    setTestResult(null);
    try {
      const res = await emailBookingsService.testParse(editingSourceId, {
        subject: testSubject, text: testBody,
      });
      setTestResult(res.parsed);
    } catch (e) {
      setTestError(e?.response?.data?.detail || t('emailBookings.sources.drawer.testFailed'));
    } finally {
      setTesting(false);
    }
  };

  const openImport = (message) => {
    setImportingMessage(message);
    const parsed = message.parsedData || {};
    setImportForm({
      roomTypeId: '',
      checkIn: parsed.check_in || '',
      checkOut: parsed.check_out || '',
      totalPrice: parsed.total_price || '',
      adults: parsed.adults || 1,
      children: parsed.children || 0,
      externalRef: message.externalRef || parsed.external_ref || '',
    });
    importModal.onOpen();
  };

  const submitImport = async () => {
    setImporting(true);
    try {
      const overrides = {};
      if (importForm.roomTypeId) overrides.roomTypeId = Number(importForm.roomTypeId);
      if (importForm.checkIn) overrides.checkIn = importForm.checkIn;
      if (importForm.checkOut) overrides.checkOut = importForm.checkOut;
      if (importForm.totalPrice !== '' && importForm.totalPrice !== null) overrides.totalPrice = String(importForm.totalPrice);
      if (importForm.adults) overrides.adults = Number(importForm.adults);
      if (importForm.children !== '' && importForm.children !== null) overrides.children = Number(importForm.children);
      if (importForm.externalRef) overrides.externalRef = importForm.externalRef;

      await emailBookingsService.importMessage(importingMessage.id, overrides);
      toast({ title: t('emailBookings.messages.toasts.imported'), status: 'success', duration: 2500 });
      importModal.onClose();
      load();
    } catch (e) {
      toast({
        title: t('emailBookings.messages.toasts.importFailed'),
        description: e?.response?.data?.detail, status: 'error', duration: 5000,
      });
    } finally {
      setImporting(false);
    }
  };

  const ignoreMessage = async (message) => {
    try {
      await emailBookingsService.ignoreMessage(message.id);
      toast({ title: t('emailBookings.messages.toasts.ignored'), status: 'success', duration: 2000 });
      load();
    } catch (e) {
      toast({ title: t('emailBookings.messages.toasts.importFailed'), status: 'error' });
    }
  };

  const openDetail = async (message) => {
    setViewingMessage(message);
    detailModal.onOpen();
    try {
      // The list row has no raw text/html (kept light); fetch the full
      // detail for the modal.
      const full = await emailBookingsService.getMessage(message.id);
      setViewingMessage(full);
    } catch (e) { /* keep the list-row fields if the detail fetch fails */ }
  };

  return (
    <PageShell
      title={t('emailBookings.page.title')}
      subtitle={t('emailBookings.page.subtitle')}
      breadcrumbs={[{ label: t('emailBookings.page.title') }]}
      loading={loading}
      error={error}
      actions={tabIndex === 0 && (
        <Button leftIcon={<AddIcon />} colorScheme="blue" size="sm" onClick={openCreateSource}>
          {t('emailBookings.sources.addButton')}
        </Button>
      )}
    >
      <Tabs index={tabIndex} onChange={setTabIndex} colorScheme="blue">
        <TabList>
          <Tab>{t('emailBookings.tabs.sources')}</Tab>
          <Tab>{t('emailBookings.tabs.messages')}</Tab>
        </TabList>
        <TabPanels>
          {/* ---------------- Sources tab ---------------- */}
          <TabPanel px={0}>
            {sources.length === 0 ? (
              <Text color="gray.500" fontSize="sm">{t('emailBookings.sources.empty')}</Text>
            ) : (
              <Box overflowX="auto">
                <Table size="sm">
                  <Thead>
                    <Tr>
                      <Th>{t('emailBookings.sources.table.name')}</Th>
                      <Th>{t('emailBookings.sources.table.forwardAddress')}</Th>
                      <Th>{t('emailBookings.sources.table.parser')}</Th>
                      <Th>{t('emailBookings.sources.table.priceMode')}</Th>
                      <Th>{t('emailBookings.sources.table.status')}</Th>
                      <Th isNumeric>{t('emailBookings.sources.table.messages')}</Th>
                      <Th>{t('emailBookings.sources.table.actions')}</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {sources.map((source) => (
                      <Tr key={source.id}>
                        <Td fontWeight="medium">{source.name}</Td>
                        <Td>
                          <HStack>
                            <Text fontFamily="mono" fontSize="xs">{source.forwardAddress}</Text>
                            <Tooltip label={t('emailBookings.sources.copyAddress')}>
                              <IconButton
                                aria-label={t('emailBookings.sources.copyAddress')}
                                icon={<CopyIcon />} size="xs" variant="ghost"
                                onClick={() => copyAddress(source.forwardAddress)}
                              />
                            </Tooltip>
                          </HStack>
                        </Td>
                        <Td>{t(`emailBookings.sources.drawer.parserOptions.${source.parser}`)}</Td>
                        <Td>{t(`emailBookings.sources.drawer.priceModeOptions.${source.priceMode}`)}</Td>
                        <Td>
                          <HStack spacing={1}>
                            <Badge colorScheme={source.isActive ? 'green' : 'gray'}>
                              {t(source.isActive ? 'emailBookings.sources.status.active' : 'emailBookings.sources.status.inactive')}
                            </Badge>
                            <Badge colorScheme={source.autoCreate ? 'blue' : 'yellow'}>
                              {t(source.autoCreate ? 'emailBookings.sources.status.autoCreate' : 'emailBookings.sources.status.reviewOnly')}
                            </Badge>
                          </HStack>
                        </Td>
                        <Td isNumeric>{source.messageCount ?? 0}</Td>
                        <Td>
                          <HStack spacing={1}>
                            <IconButton aria-label="edit" icon={<EditIcon />} size="xs" variant="ghost"
                              onClick={() => openEditSource(source)} />
                            <IconButton aria-label="delete" icon={<DeleteIcon />} size="xs" variant="ghost"
                              colorScheme="red" onClick={() => deleteSource(source)} />
                          </HStack>
                        </Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              </Box>
            )}
          </TabPanel>

          {/* ---------------- Messages tab ---------------- */}
          <TabPanel px={0}>
            <HStack mb={3} spacing={3}>
              <Select size="sm" maxW="220px" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                <option value="">{t('emailBookings.messages.filters.allStatuses')}</option>
                {Object.keys(STATUS_COLORS).map((s) => (
                  <option key={s} value={s}>{t(`emailBookings.messages.status.${s}`)}</option>
                ))}
              </Select>
              <Select size="sm" maxW="220px" value={sourceFilter} onChange={(e) => setSourceFilter(e.target.value)}>
                <option value="">{t('emailBookings.messages.filters.allSources')}</option>
                {sources.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
              </Select>
            </HStack>
            {messages.length === 0 ? (
              <Text color="gray.500" fontSize="sm">{t('emailBookings.messages.empty')}</Text>
            ) : (
              <Box overflowX="auto">
                <Table size="sm">
                  <Thead>
                    <Tr>
                      <Th>{t('emailBookings.messages.table.received')}</Th>
                      <Th>{t('emailBookings.messages.table.source')}</Th>
                      <Th>{t('emailBookings.messages.table.subject')}</Th>
                      <Th>{t('emailBookings.messages.table.status')}</Th>
                      <Th>{t('emailBookings.messages.table.reservation')}</Th>
                      <Th>{t('emailBookings.messages.table.actions')}</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {messages.map((message) => (
                      <Tr key={message.id}>
                        <Td whiteSpace="nowrap">{new Date(message.receivedAt).toLocaleString()}</Td>
                        <Td>{message.sourceName}</Td>
                        <Td maxW="280px" isTruncated>{message.subject}</Td>
                        <Td>
                          <Badge colorScheme={STATUS_COLORS[message.status] || 'gray'}>
                            {t(`emailBookings.messages.status.${message.status}`)}
                          </Badge>
                        </Td>
                        <Td>{message.reservationReference || '—'}</Td>
                        <Td>
                          <HStack spacing={1}>
                            <IconButton aria-label="view" icon={<ViewIcon />} size="xs" variant="ghost"
                              onClick={() => openDetail(message)} />
                            {message.status === 'needs_review' && (
                              <>
                                <Button size="xs" colorScheme="blue" onClick={() => openImport(message)}>
                                  {t('emailBookings.messages.importAction')}
                                </Button>
                                <Button size="xs" variant="ghost" onClick={() => ignoreMessage(message)}>
                                  {t('emailBookings.messages.ignoreAction')}
                                </Button>
                              </>
                            )}
                          </HStack>
                        </Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              </Box>
            )}
          </TabPanel>
        </TabPanels>
      </Tabs>

      {/* ---------------- Source create/edit modal ---------------- */}
      <Modal isOpen={sourceModal.isOpen} onClose={sourceModal.onClose} size="xl" scrollBehavior="inside">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            {t(editingSourceId ? 'emailBookings.sources.drawer.titleEdit' : 'emailBookings.sources.drawer.titleNew')}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack align="stretch" spacing={4}>
              <FormControl isRequired>
                <FormLabel>{t('emailBookings.sources.drawer.name')}</FormLabel>
                <Input
                  placeholder={t('emailBookings.sources.drawer.namePlaceholder')}
                  value={sourceForm.name}
                  onChange={(e) => setSourceForm({ ...sourceForm, name: e.target.value })}
                />
              </FormControl>

              {editingSourceId && (
                <FormControl>
                  <FormLabel>{t('emailBookings.sources.drawer.forwardAddressLabel')}</FormLabel>
                  <HStack>
                    <Input isReadOnly value={sources.find((s) => s.id === editingSourceId)?.forwardAddress || ''} fontFamily="mono" fontSize="sm" />
                    <IconButton
                      aria-label="copy"
                      icon={<CopyIcon />}
                      onClick={() => copyAddress(sources.find((s) => s.id === editingSourceId)?.forwardAddress)}
                    />
                  </HStack>
                  <FormHelperText>{t('emailBookings.sources.drawer.forwardAddressHelp')}</FormHelperText>
                </FormControl>
              )}

              <FormControl>
                <FormLabel>{t('emailBookings.sources.drawer.parser')}</FormLabel>
                <Select value={sourceForm.parser} onChange={(e) => setSourceForm({ ...sourceForm, parser: e.target.value })}>
                  <option value="generic_kv">{t('emailBookings.sources.drawer.parserOptions.generic_kv')}</option>
                  <option value="nomadico_bookinglayer">{t('emailBookings.sources.drawer.parserOptions.nomadico_bookinglayer')}</option>
                </Select>
              </FormControl>

              <FormControl>
                <FormLabel>{t('emailBookings.sources.drawer.allowedDomains')}</FormLabel>
                <Textarea
                  rows={2}
                  placeholder={t('emailBookings.sources.drawer.allowedDomainsPlaceholder')}
                  value={sourceForm.allowedSenderDomains}
                  onChange={(e) => setSourceForm({ ...sourceForm, allowedSenderDomains: e.target.value })}
                />
                <FormHelperText>{t('emailBookings.sources.drawer.allowedDomainsHelp')}</FormHelperText>
              </FormControl>

              <HStack spacing={4} align="start">
                <FormControl flex={1}>
                  <FormLabel>{t('emailBookings.sources.drawer.defaultRoomType')}</FormLabel>
                  <Select
                    value={sourceForm.defaultRoomType}
                    onChange={(e) => setSourceForm({ ...sourceForm, defaultRoomType: e.target.value })}
                  >
                    <option value="">{t('emailBookings.sources.drawer.defaultRoomTypeNone')}</option>
                    {roomTypes.map((rt) => <option key={rt.id} value={rt.id}>{rt.name}</option>)}
                  </Select>
                  <FormHelperText>{t('emailBookings.sources.drawer.defaultRoomTypeHelp')}</FormHelperText>
                </FormControl>
                <FormControl flex={1}>
                  <FormLabel>{t('emailBookings.sources.drawer.currency')}</FormLabel>
                  <Input
                    maxLength={3}
                    value={sourceForm.currency}
                    onChange={(e) => setSourceForm({ ...sourceForm, currency: e.target.value.toUpperCase() })}
                  />
                </FormControl>
              </HStack>

              <FormControl>
                <FormLabel>{t('emailBookings.sources.drawer.defaultSource')}</FormLabel>
                <Select
                  value={sourceForm.defaultSource}
                  onChange={(e) => setSourceForm({ ...sourceForm, defaultSource: e.target.value })}
                >
                  {['ota', 'travel_agent', 'nomadico', 'channel', 'direct', 'other'].map((opt) => (
                    <option key={opt} value={opt}>
                      {t(`emailBookings.sources.drawer.defaultSourceOptions.${opt}`)}
                    </option>
                  ))}
                </Select>
              </FormControl>

              <FormControl>
                <FormLabel>{t('emailBookings.sources.drawer.priceMode')}</FormLabel>
                <Select value={sourceForm.priceMode} onChange={(e) => setSourceForm({ ...sourceForm, priceMode: e.target.value })}>
                  <option value="total_from_email">{t('emailBookings.sources.drawer.priceModeOptions.total_from_email')}</option>
                  <option value="hotel_rates">{t('emailBookings.sources.drawer.priceModeOptions.hotel_rates')}</option>
                  <option value="balance_due_from_email">{t('emailBookings.sources.drawer.priceModeOptions.balance_due_from_email')}</option>
                </Select>
              </FormControl>

              <HStack spacing={6}>
                <FormControl display="flex" alignItems="center">
                  <FormLabel mb={0} fontSize="sm">{t('emailBookings.sources.drawer.autoCreate')}</FormLabel>
                  <Switch
                    isChecked={sourceForm.autoCreate}
                    onChange={(e) => setSourceForm({ ...sourceForm, autoCreate: e.target.checked })}
                  />
                </FormControl>
                <FormControl display="flex" alignItems="center">
                  <FormLabel mb={0} fontSize="sm">{t('emailBookings.sources.status.active')}</FormLabel>
                  <Switch
                    isChecked={sourceForm.isActive}
                    onChange={(e) => setSourceForm({ ...sourceForm, isActive: e.target.checked })}
                  />
                </FormControl>
              </HStack>
              {/* Plain Text, not FormHelperText: that hook throws outside a <FormControl>
                  and unmounted the whole app on "Add source". */}
              <Text fontSize="sm" color="gray.500" mt={-2}>{t('emailBookings.sources.drawer.autoCreateHelp')}</Text>

              <FormControl>
                <FormLabel>{t('emailBookings.sources.drawer.fieldMap')}</FormLabel>
                <Textarea
                  rows={4}
                  fontFamily="mono"
                  fontSize="xs"
                  value={sourceForm.fieldMap}
                  onChange={(e) => setSourceForm({ ...sourceForm, fieldMap: e.target.value })}
                />
                <FormHelperText>{t('emailBookings.sources.drawer.fieldMapHelp')}</FormHelperText>
              </FormControl>

              {editingSourceId && (
                <>
                  <Divider />
                  <Text fontWeight="semibold">{t('emailBookings.sources.drawer.testSection')}</Text>
                  <FormControl>
                    <FormLabel fontSize="sm">{t('emailBookings.sources.drawer.testSubject')}</FormLabel>
                    <Input value={testSubject} onChange={(e) => setTestSubject(e.target.value)} />
                  </FormControl>
                  <FormControl>
                    <FormLabel fontSize="sm">{t('emailBookings.sources.drawer.testBody')}</FormLabel>
                    <Textarea rows={6} fontFamily="mono" fontSize="xs" value={testBody} onChange={(e) => setTestBody(e.target.value)} />
                  </FormControl>
                  <Button size="sm" onClick={runTestParse} isLoading={testing} alignSelf="start">
                    {t('emailBookings.sources.drawer.testButton')}
                  </Button>
                  {testError && <Text color="red.500" fontSize="sm">{testError}</Text>}
                  {testResult && (
                    <Box borderWidth="1px" borderRadius="md" p={3} bg="gray.50">
                      <Text fontWeight="medium" fontSize="sm" mb={2}>{t('emailBookings.sources.drawer.testResultTitle')}</Text>
                      {testResult.resolved && (
                        <VStack align="stretch" spacing={1} mb={3} fontSize="sm">
                          <Text>
                            <b>{t('emailBookings.sources.drawer.testResultResolvedAction')}:</b>{' '}
                            {testResult.resolved.action || '—'}
                          </Text>
                          <Text>
                            <b>{t('emailBookings.sources.drawer.testResultResolvedRoomType')}:</b>{' '}
                            {testResult.resolved.roomType?.name || '—'}
                          </Text>
                          <Text>
                            <b>{t('emailBookings.sources.drawer.testResultResolvedRoomNumber')}:</b>{' '}
                            {testResult.resolved.roomNumber
                              || t('emailBookings.sources.drawer.testResultResolvedRoomNumberNone')}
                          </Text>
                        </VStack>
                      )}
                      <Text fontWeight="medium" fontSize="xs" color="gray.600" mb={1}>
                        {t('emailBookings.sources.drawer.testResultRawJson')}
                      </Text>
                      <Text as="pre" fontSize="xs" whiteSpace="pre-wrap">{JSON.stringify(testResult, null, 2)}</Text>
                    </Box>
                  )}
                  {!testResult && !testError && (
                    <Text fontSize="xs" color="gray.500">{t('emailBookings.sources.drawer.testEmpty')}</Text>
                  )}
                </>
              )}
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={sourceModal.onClose}>
              {t('emailBookings.sources.drawer.cancel')}
            </Button>
            <Button colorScheme="blue" onClick={saveSource} isLoading={saving}>
              {t('emailBookings.sources.drawer.save')}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* ---------------- Message import modal ---------------- */}
      <Modal isOpen={importModal.isOpen} onClose={importModal.onClose} size="lg">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{t('emailBookings.messages.importModal.title')}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack align="stretch" spacing={3}>
              {importingMessage?.error && (
                <Text fontSize="sm" color="orange.600">
                  {t('emailBookings.messages.importModal.errorNote')}: {importingMessage.error}
                </Text>
              )}
              <FormControl>
                <FormLabel fontSize="sm">{t('emailBookings.messages.importModal.roomType')}</FormLabel>
                <Select
                  placeholder={t('emailBookings.messages.importModal.roomTypeKeepParsed')}
                  value={importForm.roomTypeId || ''}
                  onChange={(e) => setImportForm({ ...importForm, roomTypeId: e.target.value })}
                >
                  {roomTypes.map((rt) => <option key={rt.id} value={rt.id}>{rt.name}</option>)}
                </Select>
              </FormControl>
              <HStack>
                <FormControl>
                  <FormLabel fontSize="sm">{t('emailBookings.messages.importModal.checkIn')}</FormLabel>
                  <Input type="date" value={importForm.checkIn || ''}
                    onChange={(e) => setImportForm({ ...importForm, checkIn: e.target.value })} />
                </FormControl>
                <FormControl>
                  <FormLabel fontSize="sm">{t('emailBookings.messages.importModal.checkOut')}</FormLabel>
                  <Input type="date" value={importForm.checkOut || ''}
                    onChange={(e) => setImportForm({ ...importForm, checkOut: e.target.value })} />
                </FormControl>
              </HStack>
              <HStack>
                <FormControl>
                  <FormLabel fontSize="sm">{t('emailBookings.messages.importModal.totalPrice')}</FormLabel>
                  <NumberInput value={importForm.totalPrice ?? ''} min={0} precision={2}
                    onChange={(val) => setImportForm({ ...importForm, totalPrice: val })}>
                    <NumberInputField />
                  </NumberInput>
                </FormControl>
                <FormControl>
                  <FormLabel fontSize="sm">{t('emailBookings.messages.importModal.adults')}</FormLabel>
                  <NumberInput value={importForm.adults ?? 1} min={1}
                    onChange={(val) => setImportForm({ ...importForm, adults: val })}>
                    <NumberInputField />
                    <NumberInputStepper>
                      <NumberIncrementStepper /><NumberDecrementStepper />
                    </NumberInputStepper>
                  </NumberInput>
                </FormControl>
                <FormControl>
                  <FormLabel fontSize="sm">{t('emailBookings.messages.importModal.children')}</FormLabel>
                  <NumberInput value={importForm.children ?? 0} min={0}
                    onChange={(val) => setImportForm({ ...importForm, children: val })}>
                    <NumberInputField />
                    <NumberInputStepper>
                      <NumberIncrementStepper /><NumberDecrementStepper />
                    </NumberInputStepper>
                  </NumberInput>
                </FormControl>
              </HStack>
              <FormControl>
                <FormLabel fontSize="sm">{t('emailBookings.messages.importModal.externalRef')}</FormLabel>
                <Input value={importForm.externalRef || ''}
                  onChange={(e) => setImportForm({ ...importForm, externalRef: e.target.value })} />
              </FormControl>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={importModal.onClose}>
              {t('emailBookings.messages.importModal.cancel')}
            </Button>
            <Button colorScheme="blue" onClick={submitImport} isLoading={importing}>
              {t('emailBookings.messages.importModal.submit')}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* ---------------- Message detail modal ---------------- */}
      <Modal isOpen={detailModal.isOpen} onClose={detailModal.onClose} size="lg" scrollBehavior="inside">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{t('emailBookings.messages.detail.title')}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {viewingMessage && (
              <VStack align="stretch" spacing={2} fontSize="sm">
                <Text><b>{t('emailBookings.messages.detail.from')}:</b> {viewingMessage.fromName} &lt;{viewingMessage.fromEmail}&gt;</Text>
                <Text><b>{t('emailBookings.messages.detail.subject')}:</b> {viewingMessage.subject}</Text>
                <Text><b>{t('emailBookings.messages.table.status')}:</b> {t(`emailBookings.messages.status.${viewingMessage.status}`)}</Text>
                {viewingMessage.error && <Text color="orange.600">{viewingMessage.error}</Text>}
                <Divider />
                <Text fontWeight="medium">{t('emailBookings.messages.detail.rawBody')}</Text>
                {/* The sender's email may have had no text/plain part at all
                    (HTML-only) — fall back to the raw HTML rather than
                    showing a blank "—" that looks like the email was empty. */}
                <Text as="pre" fontSize="xs" whiteSpace="pre-wrap" maxH="300px" overflowY="auto">
                  {viewingMessage.text || viewingMessage.html || '—'}
                </Text>
              </VStack>
            )}
          </ModalBody>
        </ModalContent>
      </Modal>
    </PageShell>
  );
}
